import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Paper,
  Button,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  People as PeopleIcon,
  Book as BookIcon,
  Delete as DeleteIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import QuizConfigurationModal from '../components/Admin/QuizConfigurationModal';
import {
  getCoreSubjects,
  getCoreSubjectAdmins,
  assignAdminsToCoreSubject,
  createCoreSubject,
  updateCoreSubject,
  deleteCoreSubject,
  createCurriculumSubject,
  getCurriculumSubjects,
} from '../services/adminCourseService';
import { getAllSystemAdmins, SystemAdminDTO } from '../services/systemAdminService';

/** Core subject (Subjects & Topics manages these; curriculum subjects are under Boards and Curriculum). */
interface CoreSubjectDTO {
  id: number;
  code: string;
  name: string;
  description?: string;
  createdAt?: string;
  updatedAt?: string;
}

const SubjectTopicManagement: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [subjects, setSubjects] = useState<CoreSubjectDTO[]>([]);
  const [allAdmins, setAllAdmins] = useState<SystemAdminDTO[]>([]);
  /** Admins keyed by core_subject_id (post-011: core-subject admins are a distinct population). */
  const [subjectAdmins, setSubjectAdmins] = useState<{ [key: number]: SystemAdminDTO[] }>({});

  // Dialog states
  const [addSubjectDialogOpen, setAddSubjectDialogOpen] = useState(false);
  const [editSubjectDialogOpen, setEditSubjectDialogOpen] = useState(false);
  const [assignAdminsDialogOpen, setAssignAdminsDialogOpen] = useState(false);
  const [deleteSubjectDialogOpen, setDeleteSubjectDialogOpen] = useState(false);
  const [selectedSubject, setSelectedSubject] = useState<CoreSubjectDTO | null>(null);

  // Quiz Config State (uses CORE_SUBJECT level directly — keyed on core_subject_id).
  const [configDialogOpen, setConfigDialogOpen] = useState(false);
  const [selectedConfigSubject, setSelectedConfigSubject] = useState<CoreSubjectDTO | null>(null);

  // Form states
  const [newSubjectCode, setNewSubjectCode] = useState('');
  const [newSubjectName, setNewSubjectName] = useState('');
  const [newSubjectDescription, setNewSubjectDescription] = useState('');
  const [savingSubject, setSavingSubject] = useState(false);
  const [selectedAdminIds, setSelectedAdminIds] = useState<number[]>([]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [coreSubjectsList, adminsList] = await Promise.all([
        getCoreSubjects(),
        getAllSystemAdmins(),
      ]);
      setSubjects(coreSubjectsList);
      setAllAdmins(adminsList);

      // Core-subject admins are queried directly post-011 (no curriculum_subject hop).
      const adminEntries = await Promise.all(
        coreSubjectsList.map(async (coreSubject) => {
          try {
            const admins = await getCoreSubjectAdmins(coreSubject.id);
            return [coreSubject.id, admins] as const;
          } catch {
            return [coreSubject.id, [] as SystemAdminDTO[]] as const;
          }
        })
      );
      setSubjectAdmins(Object.fromEntries(adminEntries));
    } catch (err: any) {
      setError(err.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenAddSubjectDialog = () => {
    setNewSubjectCode('');
    setNewSubjectName('');
    setNewSubjectDescription('');
    setAddSubjectDialogOpen(true);
  };

  const handleOpenEditSubjectDialog = (subject: CoreSubjectDTO) => {
    setSelectedSubject(subject);
    setNewSubjectCode(subject.code);
    setNewSubjectName(subject.name);
    setNewSubjectDescription(subject.description || '');
    setEditSubjectDialogOpen(true);
  };

  const handleOpenAssignAdminsDialog = (subject: CoreSubjectDTO) => {
    setSelectedSubject(subject);
    const currentAdmins = subjectAdmins[subject.id] || [];
    setSelectedAdminIds(currentAdmins.map(a => a.id));
    setAssignAdminsDialogOpen(true);
  };

  const handleOpenDeleteSubjectDialog = (subject: CoreSubjectDTO) => {
    setSelectedSubject(subject);
    setDeleteSubjectDialogOpen(true);
  };

  const handleOpenConfigDialog = (subject: CoreSubjectDTO) => {
    // Quiz config is now keyed directly on core_subject_id via the CORE_SUBJECT level.
    setSelectedConfigSubject(subject);
    setConfigDialogOpen(true);
  };

  const handleSaveSubject = async () => {
    if (!newSubjectCode.trim() || !newSubjectName.trim()) {
      setError('Subject code and name are required');
      return;
    }
    try {
      setSavingSubject(true);
      setError(null);
      const payload = {
        code: newSubjectCode.trim(),
        name: newSubjectName.trim(),
        description: newSubjectDescription.trim() || undefined,
      };

      if (selectedSubject) {
        await updateCoreSubject(selectedSubject.id, payload);
      } else {
        await createCoreSubject(payload);
      }

      await loadData();
      setAddSubjectDialogOpen(false);
      setEditSubjectDialogOpen(false);
      setSelectedSubject(null);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to save subject');
    } finally {
      setSavingSubject(false);
    }
  };

  const handleAssignAdmins = async () => {
    if (!selectedSubject) return;
    try {
      setSavingSubject(true);
      setError(null);
      await assignAdminsToCoreSubject(selectedSubject.id, selectedAdminIds);
      await loadData();
      setAssignAdminsDialogOpen(false);
      setSelectedSubject(null);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to assign admins');
    } finally {
      setSavingSubject(false);
    }
  };

  /**
   * Bridge for the legacy topics route (`/admin/subjects/{curriculumSubjectId}/topics`),
   * which is still keyed on curriculum_subject_id. Migrating that route to a core-subject
   * key is tracked separately and intentionally out of scope for this plan.
   */
  const ensureCurriculumSubjectForTopics = async (coreSubject: CoreSubjectDTO): Promise<number> => {
    const curriculumSubjects = await getCurriculumSubjects();
    const linked = curriculumSubjects.find((cs) => cs.coreSubjectId === coreSubject.id);
    if (linked) {
      return linked.id;
    }
    const created = await createCurriculumSubject({
      coreSubjectId: coreSubject.id,
      code: coreSubject.code,
      name: coreSubject.name,
      description: coreSubject.description,
    });
    return created.id;
  };

  const handleDeleteSubject = async () => {
    if (!selectedSubject) return;
    try {
      setSavingSubject(true);
      setError(null);
      await deleteCoreSubject(selectedSubject.id);
      await loadData();
      setDeleteSubjectDialogOpen(false);
      setSelectedSubject(null);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to delete subject');
      setDeleteSubjectDialogOpen(false);
      setSelectedSubject(null);
    } finally {
      setSavingSubject(false);
    }
  };

  const handleManageTopics = async (subject: CoreSubjectDTO) => {
    try {
      const curriculumSubjectId = await ensureCurriculumSubjectForTopics(subject);
      navigate(`/admin/subjects/${curriculumSubjectId}/topics`);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to open topics');
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, textAlign: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" component="h1">
            Subjects & Topics
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage core subjects and assign system admins to manage topics for each subject.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenAddSubjectDialog}
        >
          Add Subject
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 88 }}><strong>ID</strong></TableCell>
              <TableCell><strong>Code</strong></TableCell>
              <TableCell><strong>Name</strong></TableCell>
              <TableCell><strong>Description</strong></TableCell>
              <TableCell><strong>Assigned Admins</strong></TableCell>
              <TableCell align="right"><strong>Actions</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {subjects.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                    No core subjects defined yet. Use &quot;Add Subject&quot; to create one.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              subjects.map((subject) => {
                const admins = subjectAdmins[subject.id] || [];
                return (
                  <TableRow key={subject.id} hover>
                    <TableCell>
                      <Typography component="span" variant="body2" sx={{ fontFamily: 'monospace' }}>
                        {subject.id}
                      </Typography>
                    </TableCell>
                    <TableCell><strong>{subject.code}</strong></TableCell>
                    <TableCell>{subject.name}</TableCell>
                    <TableCell>
                      {subject.description ? (
                        <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: 300 }}>
                          {subject.description}
                        </Typography>
                      ) : (
                        <Typography variant="body2" color="text.secondary" fontStyle="italic">
                          No description
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {admins.length === 0 ? (
                        <Typography variant="body2" color="text.secondary" fontStyle="italic">
                          No admins assigned
                        </Typography>
                      ) : (
                        <Box display="flex" gap={0.5} flexWrap="wrap">
                          {admins.map((admin) => (
                            <Chip
                              key={admin.id}
                              label={`${admin.firstName} ${admin.lastName}`}
                              size="small"
                              variant="outlined"
                            />
                          ))}
                        </Box>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={() => handleOpenEditSubjectDialog(subject)}
                        title="Edit Subject"
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleOpenAssignAdminsDialog(subject)}
                        title="Assign Admins"
                      >
                        <PeopleIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleManageTopics(subject)}
                        title="Manage Topics"
                      >
                        <BookIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleOpenConfigDialog(subject)}
                        title="Configure Quiz Settings"
                        color="primary"
                      >
                        <SettingsIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDeleteSubjectDialog(subject)}
                        title="Delete Subject"
                        color="error"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Quiz Configuration Modal — core-subject layer (universal baseline). */}
      {selectedConfigSubject && (
        <QuizConfigurationModal
          open={configDialogOpen}
          onClose={() => {
            setConfigDialogOpen(false);
            setSelectedConfigSubject(null);
          }}
          level="CORE_SUBJECT"
          entityId={selectedConfigSubject.id}
          entityName={selectedConfigSubject.name}
        />
      )}

      {/* Add Subject Dialog */}
      <Dialog
        open={addSubjectDialogOpen}
        onClose={() => setAddSubjectDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add Core Subject</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Subject Code (e.g., MTH, SCI)"
              fullWidth
              value={newSubjectCode}
              onChange={(e) => setNewSubjectCode(e.target.value)}
              required
            />
            <TextField
              label="Subject Name"
              fullWidth
              value={newSubjectName}
              onChange={(e) => setNewSubjectName(e.target.value)}
              required
            />
            <TextField
              label="Description"
              fullWidth
              multiline
              rows={3}
              value={newSubjectDescription}
              onChange={(e) => setNewSubjectDescription(e.target.value)}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddSubjectDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleSaveSubject}
            variant="contained"
            disabled={savingSubject}
          >
            {savingSubject ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Subject Dialog */}
      <Dialog
        open={editSubjectDialogOpen}
        onClose={() => {
          setEditSubjectDialogOpen(false);
          setSelectedSubject(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Core Subject</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Subject Code (e.g., MTH, SCI)"
              fullWidth
              value={newSubjectCode}
              onChange={(e) => setNewSubjectCode(e.target.value)}
              required
            />
            <TextField
              label="Subject Name"
              fullWidth
              value={newSubjectName}
              onChange={(e) => setNewSubjectName(e.target.value)}
              required
            />
            <TextField
              label="Description"
              fullWidth
              multiline
              rows={3}
              value={newSubjectDescription}
              onChange={(e) => setNewSubjectDescription(e.target.value)}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setEditSubjectDialogOpen(false);
            setSelectedSubject(null);
          }}>Cancel</Button>
          <Button
            onClick={handleSaveSubject}
            variant="contained"
            disabled={savingSubject}
          >
            {savingSubject ? 'Updating...' : 'Update'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Assign Admins Dialog */}
      <Dialog
        open={assignAdminsDialogOpen}
        onClose={() => {
          setAssignAdminsDialogOpen(false);
          setSelectedSubject(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Assign System Admins - {selectedSubject?.name}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
            {allAdmins.map((admin) => (
              <Box
                key={admin.id}
                display="flex"
                alignItems="center"
                sx={{
                  p: 1,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  cursor: 'pointer',
                  bgcolor: selectedAdminIds.includes(admin.id) ? 'action.selected' : 'background.paper',
                  '&:hover': { bgcolor: 'action.hover' },
                }}
                onClick={() => {
                  if (selectedAdminIds.includes(admin.id)) {
                    setSelectedAdminIds(selectedAdminIds.filter(id => id !== admin.id));
                  } else {
                    setSelectedAdminIds([...selectedAdminIds, admin.id]);
                  }
                }}
              >
                <input
                  type="checkbox"
                  checked={selectedAdminIds.includes(admin.id)}
                  onChange={() => {
                    if (selectedAdminIds.includes(admin.id)) {
                      setSelectedAdminIds(selectedAdminIds.filter(id => id !== admin.id));
                    } else {
                      setSelectedAdminIds([...selectedAdminIds, admin.id]);
                    }
                  }}
                  style={{ marginRight: 8 }}
                />
                <Typography variant="body2">
                  {admin.firstName} {admin.lastName} ({admin.email})
                  {admin.isSuperAdmin && (
                    <Chip label="Super Admin" size="small" color="primary" sx={{ ml: 1 }} />
                  )}
                </Typography>
              </Box>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setAssignAdminsDialogOpen(false);
            setSelectedSubject(null);
          }}>Cancel</Button>
          <Button
            onClick={handleAssignAdmins}
            variant="contained"
            disabled={savingSubject}
          >
            {savingSubject ? 'Saving...' : 'Save Assignments'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Subject Dialog */}
      <Dialog
        open={deleteSubjectDialogOpen}
        onClose={() => {
          setDeleteSubjectDialogOpen(false);
          setSelectedSubject(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Delete Subject</DialogTitle>
        <DialogContent>
          <Typography variant="body1" paragraph>
            Are you sure you want to delete the subject <strong>{selectedSubject?.name}</strong>?
          </Typography>
          <Alert severity="warning">
            This action is <strong>irreversible</strong>.
            All assigned admins will be unassigned.
            <br /><br />
            <strong>Note:</strong> Deletion will be blocked if there are topics or courses linked to this subject.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setDeleteSubjectDialogOpen(false);
            setSelectedSubject(null);
          }}>Cancel</Button>
          <Button
            onClick={handleDeleteSubject}
            variant="contained"
            color="error"
            disabled={savingSubject}
          >
            {savingSubject ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default SubjectTopicManagement;
